import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Download, Eye, FileText, AlertCircle } from 'lucide-react';
import { getInvoicesByCompanyId } from '../../services/storage';
import type { Invoice } from '../../types';

export const ClientInvoices = () => {
  const { company } = useAuth();
  const invoices: Invoice[] = company ? getInvoicesByCompanyId(company.id) : [];

  const getStatusBadge = (status: Invoice['status']) => {
    const variants = {
      paid: 'success',
      pending: 'secondary',
      overdue: 'default',
      canceled: 'default'
    } as const;
    
    const labels = {
      paid: 'Pago',
      pending: 'Pendente',
      overdue: 'Vencido',
      canceled: 'Cancelado'
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  if (invoices.length === 0) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Minhas Faturas | Portal Ethics</title>
        </Helmet>
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Nenhuma fatura encontrada</h2>
          <p className="text-neutral-600">Você ainda não possui faturas.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Minhas Faturas | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Minhas Faturas</h1>
          <p className="text-neutral-600">Visualize e baixe suas faturas</p>
        </div>

        {/* Invoices List */}
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id} className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-primary-blue" />
                    <span className="font-semibold text-neutral-900">{invoice.invoiceNumber}</span>
                    {getStatusBadge(invoice.status)}
                    {invoice.status === 'pending' && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Aguardando pagamento
                      </Badge>
                    )}
                  </div>
                  <p className="text-neutral-600 mb-1">{invoice.description}</p>
                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                    <span>Vencimento: {new Date(invoice.dueDate).toLocaleDateString('pt-BR')}</span>
                    {invoice.paidDate && (
                      <span>Pago em: {new Date(invoice.paidDate).toLocaleDateString('pt-BR')}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-neutral-900">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(invoice.amount)}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Visualizar
                    </Button>
                    <Button variant="outline" size="sm" className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Baixar
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};
